import { Trash2 } from 'lucide-react';
import { ConfirmDialog } from './ConfirmDialog';

interface Props {
  /** Human-readable resource category, e.g. "command", "script". Lowercased. */
  resourceKind: string;
  /** Specific resource identifier shown in the dialog body (bolded). */
  resourceName: string;
  /** Disables the trigger (e.g. when the resource is a bundled default). */
  disabled?: boolean;
  /**
   * Invoked when the user confirms. See `ConfirmDialog` for the
   * fire-and-forget error contract — errors must be surfaced at the
   * page level, not from here.
   */
  onConfirm: () => void | Promise<void>;
}

/**
 * Standard "trash icon → confirm dialog → delete" pattern used by the
 * Commands, Scripts, MCP Configs, and Skills editor pages. Exists to keep
 * the destructive-delete UX consistent and prevent each page from drifting
 * its own copy of the trigger styling and copy.
 */
export function ResourceDeleteConfirm({
  resourceKind,
  resourceName,
  disabled,
  onConfirm,
}: Props): React.ReactElement {
  return (
    <ConfirmDialog
      trigger={
        <button
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-md bg-error/25 px-3 py-1 text-sm text-error disabled:opacity-50"
          aria-label={`Delete ${resourceKind}`}
        >
          <Trash2 className="size-4" />
        </button>
      }
      title={`Delete ${resourceKind}?`}
      description={
        <>
          Permanently delete the {resourceKind} <strong>{resourceName}</strong>. This cannot be
          undone.
        </>
      }
      confirmLabel="Delete"
      onConfirm={onConfirm}
    />
  );
}
